import { useMemo, type ReactNode } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Legend,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import type { CampaignCardView } from "../../../shared/types/campaign-card.type";
import {
	fmtDec,
	fmtMoney,
	placementColor,
} from "../../../shared/utils/campaign-metrics-format";
import { PlacementsRadarChart } from "./PlacementsRadarChart";

type Props = {
	campaign: CampaignCardView;
};

const tooltipStyle = {
	backgroundColor: "var(--window-background-color)",
	border: "1px solid var(--window-border-color)",
	borderRadius: 6,
	fontSize: 11,
	color: "var(--main-text-color)",
};

export const AnalyticsSection = ({ campaign }: Props) => {
	const placementSummary = useMemo(() => {
		const acc: Record<
			string,
			{
				cost: number;
				impr: number;
				inst: number;
				reg: number;
				dep: number;
				dep_sum: number;
				profit: number;
			}
		> = {};
		campaign.adgroups.forEach((ag) => {
			ag.placements.forEach((p) => {
				if (!acc[p.name]) {
					acc[p.name] = {
						cost: 0,
						impr: 0,
						inst: 0,
						reg: 0,
						dep: 0,
						dep_sum: 0,
						profit: 0,
					};
				}
				acc[p.name].cost += p.metrics.cost;
				acc[p.name].impr += p.metrics.impr;
				acc[p.name].inst += p.metrics.inst;
				acc[p.name].reg += p.metrics.reg;
				acc[p.name].dep += p.metrics.dep;
				acc[p.name].dep_sum += p.metrics.dep_sum;
				acc[p.name].profit += p.metrics.profit;
			});
		});
		const total =
			Object.values(acc).reduce((s, x) => s + x.cost, 0) || 1;
		return Object.entries(acc)
			.map(([name, v]) => ({
				name,
				fill: placementColor(name),
				...v,
				pct: (v.cost / total) * 100,
			}))
			.filter((p) => p.cost > 0);
	}, [campaign]);

	const radarData = placementSummary.map((p) => ({
		placement: p.name,
		cost: p.cost,
	}));

	const adgroupChartData = campaign.adgroups.map((ag) => ({
		name: ag.name.replace("AG_", ""),
		cost: ag.metrics.cost,
		profit: ag.metrics.profit,
	}));

	return (
		<section>
			<div className='campaign-detail__section-title'>Analytics</div>
			<div className='campaign-detail__chart-grid'>
				<ChartCard title='Cost vs Profit by Ad Group'>
					<ResponsiveContainer width='100%' height='100%'>
						<BarChart data={adgroupChartData}>
							<CartesianGrid
								stroke='var(--window-border-color)'
								strokeDasharray='3 3'
							/>
							<XAxis
								dataKey='name'
								tick={{ fill: "var(--secondary-text-color)", fontSize: 10 }}
							/>
							<YAxis
								tick={{ fill: "var(--secondary-text-color)", fontSize: 10 }}
							/>
							<Tooltip
								contentStyle={tooltipStyle}
								formatter={(value, name) => {
									const raw =
										typeof value === "number"
											? value
											: Number(value);
									return [
										fmtMoney(Number.isFinite(raw) ? raw : 0),
										name === "profit" ? "Profit" : "Cost",
									];
								}}
							/>
							<Bar
								dataKey='cost'
								name='Cost'
								fill='#3db8d4'
								radius={[4, 4, 0, 0]}
							/>
							<Bar
								dataKey='profit'
								name='Profit'
								fill='var(--success-color)'
								radius={[4, 4, 0, 0]}
							/>
						</BarChart>
					</ResponsiveContainer>
				</ChartCard>

				<ChartCard title='Placement Share (% of cost)'>
					<ResponsiveContainer width='100%' height='100%'>
						<PieChart>
							<Pie
								data={placementSummary}
								dataKey='cost'
								nameKey='name'
								innerRadius={50}
								outerRadius={80}
								paddingAngle={2}>
								{placementSummary.map((p) => (
									<Cell key={p.name} fill={p.fill} />
								))}
							</Pie>
							<Tooltip
								contentStyle={tooltipStyle}
								formatter={(value, name, item) => {
									const raw =
										typeof value === "number"
											? value
											: Number(value);
									const pct = Number(
										(item?.payload as { pct?: number } | undefined)
											?.pct ?? 0,
									);
									return [
										`${fmtMoney(Number.isFinite(raw) ? raw : 0)} (${fmtDec(pct, 2)}%)`,
										name,
									];
								}}
							/>
							<Legend wrapperStyle={{ fontSize: 11, color: "var(--main-text-color)" }} />
						</PieChart>
					</ResponsiveContainer>
				</ChartCard>

				<ChartCard title='Placements — Radar'>
					<PlacementsRadarChart data={radarData} />
				</ChartCard>
			</div>
		</section>
	);
};

function ChartCard({
	title,
	children,
}: {
	title: string;
	children: ReactNode;
}) {
	return (
		<div className='campaign-detail__chart-card'>
			<div className='campaign-detail__chart-card-title'>{title}</div>
			<div className='campaign-detail__chart-height'>{children}</div>
		</div>
	);
}
